import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { SchemaField, FieldDataType, dataTypeLabels, formatSnakeCase, WorkflowStepAction } from 'src/app/modules/core/store/models';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Language, TranslationResult, DEFAULT_APP_CULTURE_CODE, TranslationRequest } from 'src/app/modules/core/languages/language.model';
import { catchError, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { cloneDeep } from 'lodash-es';
import { LabelTranslationService } from 'src/app/modules/core/languages/label-translation.service';
import { UserProfile } from 'src/app/modules/core/store/user-profile';

@Component({
  selector: 'topo-action-label-translations',
  templateUrl: './action-label-translations.component.html',
  styleUrls: ['./action-label-translations.component.scss'],
  standalone: false
})
export class ActionLabelTranslationsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private _actions: WorkflowStepAction[];
  @Input() set actions(values: WorkflowStepAction[]) {
    if (!!values) {
      this._actions = cloneDeep(values);
    }
    if (!!this._actions && this._actions.length) {
      this.loadLanguages();
    }
  }
  get actions(): WorkflowStepAction[] {
    return this._actions;
  }

  @Input()
  get isActive(): boolean {
    return !!this._actions;
  }

  @Output() updateTranslations = new EventEmitter<WorkflowStepAction[]>();
  @Output() cancel = new EventEmitter<void>();

  languages: Language[];
  sourceLanguages: Language[];
  targetLanguages: Language[];
  sourceCulture: string;
  targetCulture: string;
  sourceValues: { [uid: string]: string };
  targetValues: { [uid: string]: string };
  stringsToTranslate: TranslationRequest[];
  userProfile: UserProfile;

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private labelTranslationService: LabelTranslationService
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromUserProfile.selectProfileUser)
      .pipe(withLatestFrom(this.store.select(fromUserProfile.selectUserLanguages)), takeUntil(this.destroy$))
      .subscribe(([userProfile, languages]) => {
        this.languages = languages;
        this.userProfile = userProfile;
        this.loadLanguages();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLanguageDisplay(lang: Language): string {
    if (!!lang) {
      return this.translateService.instant(`LANGUAGES.NATIVE.LANGUAGE-${lang.cultureCode}`);
    }
    return '';
  }

  getSourceFieldDisplay(action: WorkflowStepAction): string {
    return action.label;
  }

  getTargetFieldDisplay(action: WorkflowStepAction): string {
    return action.label;
  }

  sourceCultureSelected($event: any): void {
    this.targetLanguages = [];
    this.languages.forEach(l => {
      if (l.cultureCode !== this.sourceCulture) {
        this.targetLanguages.push(l);
      }
    });
    if (this.targetLanguages.length) {
      const userLanguage = this.targetLanguages.find(t => t.cultureCode === this.userProfile?.culture?.cultureCode);
      if (!!userLanguage) {
        this.targetCulture = userLanguage.cultureCode;
      } else {
        this.targetCulture = this.targetLanguages[0].cultureCode;
      }
    } else {
      this.targetCulture = null;
    }
    this.loadValues();
  }

  targetCultureSelected($event: any): void {
    this.loadValues();
  }

  fieldChanged(event: any, action: WorkflowStepAction): void {
    const value = event.target.value;

    this.updateFieldValue(value, action);
  }

  private updateFieldValue(value: string, action: WorkflowStepAction): void {
    if (!!!action.labelTranslations) {
      action.labelTranslations = [];
    }

    // empty string, remove translation
    if (!(value?.trim().length > 0)) {
      const index = action.labelTranslations.findIndex(t => t.cultureCode == this.targetCulture);
      if (index >= 0) {
        action.labelTranslations.splice(index, 1);
      }
    } else {
      const translation = action.labelTranslations.find(t => t.cultureCode === this.targetCulture);
      if (!!translation) {
        translation.name = value;
      } else {
        action.labelTranslations.push({
          cultureCode: this.targetCulture,
          name: value
        });
      }
    }
  }

  autoTranslateAll(): void {
    this.getStringTranslations(this.actions);
  }

  autoTranslateField(action: WorkflowStepAction): void {
    this.getStringTranslations([action]);
  }

  getStringTranslations(actions: WorkflowStepAction[]): void {
    this.stringsToTranslate = [];

    actions.forEach(f => {
      const sourceValue = this.sourceValues[f.uId];
      if (sourceValue?.trim().length > 0) {
        this.stringsToTranslate.push({
          text: sourceValue,
          id: f.uId
        });
      }
    });

    if (this.stringsToTranslate.length) {
      this.getTranslations(this.sourceCulture, this.targetCulture, this.stringsToTranslate)
        .pipe(takeUntil(this.destroy$))
        .subscribe(results => {
          if (!!results) {
            results.forEach((value, index) => {
              this.targetValues[value.id] = value.text;
              const field = this.actions.find(f => f.uId === value.id);
              this.updateFieldValue(value.text, field);
            });
          }
        });
    }
  }

  private getTranslations(fromCode: string, toCode: string, strings: TranslationRequest[]): Observable<TranslationResult[]> {
    return this.labelTranslationService.getTranslatedDatasetStrings(fromCode, toCode, strings).pipe(
      catchError(e => {
        console.error(e);
        return of(null);
      }),
      map(data => {
        return data;
      })
    );
  }

  private loadLanguages(): void {
    if (!!this.languages && this.languages.length) {
      const sourceLanguages = [];
      this.sourceLanguages = [];
      this.targetLanguages = [];
      this.actions.forEach(f => {
        const culture = f.labelCultureCode || DEFAULT_APP_CULTURE_CODE;
        if (!sourceLanguages.some(l => l.cultureCode === culture)) {
          const lang = this.languages.find(l => l.cultureCode === culture);
          if (!!lang) {
            sourceLanguages.push(lang);
          }
        }
        if (!!f.labelTranslations) {
          f.labelTranslations.forEach(t => {
            if (!sourceLanguages.some(l => l.cultureCode === t.cultureCode)) {
              const lang = this.languages.find(l => l.cultureCode === t.cultureCode);
              if (!!lang) {
                sourceLanguages.push(lang);
              }
            }
          });
        }
      });

      this.languages.forEach(l => {
        if (sourceLanguages.some(sl => sl.cultureCode === l.cultureCode)) {
          this.sourceLanguages.push(l);
        }
      });

      if (this.sourceLanguages.length) {
        this.sourceCulture = this.sourceLanguages[0].cultureCode;
      } else {
        this.sourceCulture = null;
      }

      this.sourceCultureSelected(null);
    }

    this.loadValues();
  }

  private loadValues(): void {
    this.sourceValues = {};
    this.targetValues = {};

    if (!!this.actions && this.actions.length) {
      this.actions.forEach(f => {
        if (!!!f.labelTranslations) {
          f.labelTranslations = [];
        }
        this.sourceValues[f.uId] = this.getFieldDisplay(f, this.sourceCulture);
        this.targetValues[f.uId] = this.getFieldDisplay(f, this.targetCulture);
      });
    }
  }

  private getFieldDisplay(action: WorkflowStepAction, cultureCode?: string): string {
    const displayNameCulture = action.labelCultureCode || DEFAULT_APP_CULTURE_CODE;

    if (cultureCode === null || displayNameCulture === cultureCode) {
      return action.label;
    }

    if (!!action.labelTranslations) {
      const translation = action.labelTranslations.find(t => t.cultureCode == cultureCode);
      if (!!translation) {
        return translation.name;
      }
    }

    return '';
  }

  close(): void {
    this.cancel.emit();
  }

  cancelClick(): void {
    this.cancel.emit();
  }

  saveClick(): void {
    this.updateTranslations.emit(this.actions);
  }
}
